import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Text } from '@components/base/display';
import { Button } from '@components/base/actions';
import { HStack, VStack } from '@components/base/layout';
import { Alert, Modal, useToast } from '@components/base/feedback';
import { usersService } from '../../api/usersService';
import type { User } from '../../api/usersService';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ResetPasswordModalProps {
  /** The user whose password is reset; `null` means the modal is closed. */
  readonly user: User | null;
  readonly onClose: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FALLBACK_ERROR = 'An unexpected error occurred.';

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError<{ message?: unknown }>(error)) {
    const apiMessage = error.response?.data.message;
    if (typeof apiMessage === 'string' && apiMessage !== '') {
      return apiMessage;
    }
  }
  if (error instanceof Error && error.message !== '') {
    return error.message;
  }
  return FALLBACK_ERROR;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Confirmation modal for the admin "Reset password" action. Admin chrome with
 * no tenant-facing fields (same exemption as `AdminListPage`'s `DeleteModal`),
 * so it is intentionally not registered as a configurable component.
 */
export function ResetPasswordModal({ user, onClose }: ResetPasswordModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: (id: number) => usersService.resetPassword(id),
    onSuccess: (result) => {
      toast({
        variant: 'success',
        title: 'New password sent',
        message: `Sent to ${result.data.sent_to}.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      handleClose();
    },
  });

  function handleClose() {
    mutation.reset();
    onClose();
  }

  function handleConfirm() {
    if (user) {
      mutation.mutate(user.id);
    }
  }

  const isPending = mutation.isPending;

  return (
    <Modal open={user !== null} onClose={handleClose} size="sm">
      <Modal.Header title="Reset Password" />
      <Modal.Body>
        <VStack gap={3}>
          <Text>
            A new strong password will be generated for{' '}
            <Text as="span" weight="bold">
              {user?.name ?? ''}
            </Text>{' '}
            and sent to{' '}
            <Text as="span" weight="bold">
              {user?.email ?? ''}
            </Text>
            . The current password stops working immediately.
          </Text>
          {mutation.isError && (
            <Alert variant="error" title="Reset failed">
              {getErrorMessage(mutation.error)}
            </Alert>
          )}
        </VStack>
      </Modal.Body>
      <Modal.Footer>
        <HStack gap={2} justify="end">
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={isPending} onClick={handleConfirm}>
            Send new password
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
}
